import {
  CloseButton,
  Dialog,
  HStack,
  Icon,
  Portal,
  Spacer,
  Spinner,
  Stack,
  StackSeparator,
  Text
} from '@chakra-ui/react'
import type { FC } from 'react'
import { PiCheck, PiX } from 'react-icons/pi'

import { Button } from '@app/components/ui/button'
import { Tooltip } from '@app/components/ui/tooltip'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import { TaskStatusesEnum, TaskTypesEnum } from '@graphql/types'

interface Props {
  strategyId: string
}

const BulkImportStatusModal: FC<Props> = ({ strategyId }) => {
  const bulkImportTasks = useGetObjectsByProperties('task', {
    domainObjectType: 'Strategy',
    domainObjectId: strategyId,
    taskType: TaskTypesEnum.BulkUrlImport
  })

  if (!bulkImportTasks.length) {
    return null
  }

  const statusElement = (status: TaskStatusesEnum) => {
    if (status === TaskStatusesEnum.Scheduled) {
      return (
        <Tooltip content="Scheduled" showArrow>
          <Spinner size="xs" />
        </Tooltip>
      )
    } else if (status === TaskStatusesEnum.InProgress) {
      return (
        <Tooltip content="In progress" showArrow>
          <Spinner size="xs" />
        </Tooltip>
      )
    } else if (status === TaskStatusesEnum.Completed) {
      return (
        <Tooltip content="Completed" showArrow>
          <Icon color="green.500" size="sm">
            <PiCheck />
          </Icon>
        </Tooltip>
      )
    } else if (status === TaskStatusesEnum.Failed) {
      return (
        <Tooltip content="Failed" showArrow>
          <Icon color="red.500" size="sm">
            <PiX />
          </Icon>
        </Tooltip>
      )
    }
  }

  return (
    <Dialog.Root placement="center" size="lg">
      <Dialog.Trigger asChild>
        {/* mt is to match spacing on the map nav tabs */}
        <Button colorPalette="teal" size="md" mt={0.5}>
          Bulk Import Status
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Bulk Import Status</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body maxH="450px" overflowY="auto">
              <Stack gap={3} separator={<StackSeparator />}>
                {bulkImportTasks
                  .sort((a, b) => parseInt(b.id, 10) - parseInt(a.id, 10))
                  .map((task) => (
                    <HStack key={task.id}>
                      <Text wordBreak="break-word" color={task.status === TaskStatusesEnum.Failed ? 'fg.error' : 'fg'}>
                        {task.summary || task.description}
                      </Text>
                      <Spacer />
                      {statusElement(task.status)}
                    </HStack>
                  ))}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Close</Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default BulkImportStatusModal
